/**
 * Field advertising approval of record.
 * SCOPE: three-pillar educational architecture ONLY.
 * NOT approved: vendor/carrier identification, product names,
 * pricing, rates, or any solicitation content on this site.
 * Product-specific discussion occurs in consultation only.
 */
export const COMPLIANCE_APPROVAL = {
  approved: true,
  // Fill these in with the real approval record. Never use angle-bracket
  // placeholders — the build guard fails the build on them.
  reviewer: null as string | null,
  referenceNumber: null as string | null,
  approvalDate: null as string | null,
  scopeGranted: "Three-pillar educational content architecture.",
  scopeExcluded: [
    "Naming any broker-dealer, FMO, IMO, or upline organization",
    "Naming any insurance carrier",
    "Naming specific insurance products or product categories",
    "Any pricing, premium, rate, or cost figure",
    "Any illustration, projection, or return figure",
    "Any solicitation or application content",
  ],
  onDomainConversionApproved: false,
  expiresOn: null,
  documentPath: "docs/compliance/approval.pdf",
} as const;

/**
 * COMPLIANCE LEVEL — ordered, least permissive first.
 *
 * education_only                  → education content only. No CTAs out, no forms.
 * education_plus_external         → education + CTAs linking OUT to the approved
 *                                   consultation destination. No on-domain forms.
 * education_plus_onsite_consult   → education + on-domain consultation request form
 *                                   and scheduler. Still NO vendor names, product
 *                                   names, pricing, or illustrations.
 * full                            → defined but UNUSED. Nothing sets it.
 */
export type ComplianceLevel =
  | "education_only"
  | "education_plus_external"
  | "education_plus_onsite_consult"
  | "full";

export const COMPLIANCE_LEVEL_ORDER: ComplianceLevel[] = [
  "education_only",
  "education_plus_external",
  "education_plus_onsite_consult",
  "full",
];

// Set from the approval record above. Do not set independently.
export const COMPLIANCE_LEVEL: ComplianceLevel =
  COMPLIANCE_APPROVAL.onDomainConversionApproved
    ? "education_plus_onsite_consult"
    : "education_plus_external";

/**
 * Off-domain consultation destination used below `education_plus_onsite_consult`.
 * Set to the approved external URL when supplied. Until then, CTAs fall back to
 * the on-domain /contact page so no link is ever dead.
 */
const APPROVED_CONSULT_URL_RAW: string | null = null;

export const APPROVED_CONSULT_URL: string = APPROVED_CONSULT_URL_RAW ?? "/contact";

/** True only when the consultation destination is a real off-domain URL. */
export const CONSULT_URL_IS_EXTERNAL = /^https?:\/\//i.test(APPROVED_CONSULT_URL);

/** Georgia producer license number. Renders nothing until supplied. */
export const LICENSE_NUMBER: string | null = null;

/** The single consultation CTA label used everywhere on the site. */
export const CONSULT_CTA_LABEL = "Request a Consultation";

/** Widened comparison helper — keeps checks readable without literal narrowing. */
export function isLevel(level: ComplianceLevel): boolean {
  return (COMPLIANCE_LEVEL as ComplianceLevel) === level;
}

/** True when the active level is at least `level`. */
export function atLeastLevel(level: ComplianceLevel): boolean {
  return (
    COMPLIANCE_LEVEL_ORDER.indexOf(COMPLIANCE_LEVEL) >=
    COMPLIANCE_LEVEL_ORDER.indexOf(level)
  );
}

/** Legacy boolean, preserved so every existing gate keeps working. */
export const COMPLIANCE_MODE = !isLevel("full");

/** On-domain booking + lead capture. */
export const ALLOW_ON_DOMAIN_BOOKING = atLeastLevel("education_plus_onsite_consult");

/** External consultation CTAs. */
export const ALLOW_EXTERNAL_STRATEGIST_CTA =
  atLeastLevel("education_plus_external") && !ALLOW_ON_DOMAIN_BOOKING;
